import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Bell } from 'lucide-react';

/**
 * Compact email-capture form for location-based alerts on state/city
 * hub pages. Subscribers get a single email when a matching instructor
 * joins or a matching event is posted, then the subscription is
 * cleared.
 *
 * Props:
 *  - type: 'instructor' | 'event'
 *  - stateSlug: required (e.g. 'georgia')
 *  - citySlug: optional (e.g. 'atlanta-ga')
 *  - locationLabel: human-readable label (e.g. 'Atlanta, GA' or 'Georgia')
 */
export function LocationAlertSignup({ type, stateSlug, citySlug, locationLabel }) {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const what = type === 'instructor' ? 'a new instructor joins' : 'a new event is posted';
  const buttonLabel = type === 'instructor' ? 'Alert me' : 'Alert me';
  const accent = type === 'instructor' ? '#b456df' : '#56dfb4';
  const accentText = type === 'instructor' ? 'white' : '#0f172a';

  async function submit(e) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error('Please enter a valid email');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/location-alerts', {
        email: trimmed,
        type,
        state: stateSlug,
        city: citySlug || null,
      });
      setDone(true);
      toast.success(`You're on the list for ${locationLabel}!`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save — please try again');
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div
        className="mt-8 p-6 rounded-2xl border border-slate-100 bg-slate-50 text-center"
        data-testid={`location-alert-thankyou-${type}`}
      >
        <Bell className="w-6 h-6 mx-auto mb-2" style={{ color: accent }} />
        <p className="text-sm text-slate-700">
          You're on the list — we'll email <span className="font-semibold">{email}</span> the moment {what} in {locationLabel}.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="mt-8 p-6 rounded-2xl border border-slate-100 bg-white"
      data-testid={`location-alert-form-${type}`}
    >
      <div className="flex items-center gap-2 mb-1">
        <Bell className="w-4 h-4" style={{ color: accent }} />
        <h3
          className="text-base font-semibold"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          {type === 'instructor'
            ? `Get notified when an instructor joins ${locationLabel}`
            : `Alert me when events are added in ${locationLabel}`}
        </h3>
      </div>
      <p className="text-xs text-slate-500 mb-3">
        One email when {what}. No spam, ever.
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          required
          autoComplete="email"
          className="rounded-full flex-1"
          data-testid={`location-alert-email-${type}`}
        />
        <Button
          type="submit"
          disabled={submitting}
          className="rounded-full whitespace-nowrap"
          style={{ background: accent, color: accentText }}
          data-testid={`location-alert-submit-${type}`}
        >
          {submitting ? 'Saving…' : buttonLabel}
        </Button>
      </div>
    </form>
  );
}
